import qs from 'qs';

import { endpoint } from '@/services/api/endpoint';

import {
  AcceptResultPathSchema,
  AcceptResultResponseSchema,
  DocumentsAggregationsQuerySchema,
  DocumentsAggregationsResponseSchema,
  SubtypeCatalogResponseSchema,
  TagSuggestionsQuerySchema,
  TagSuggestionsResponseSchema,
  RecentTagsResponseSchema,
  ExportFormatsResponseSchema,
  SearchFavoritesResponseSchema,
  SaveSearchCriteriaBodySchema,
  DeleteSearchCriteriaBodySchema,
  SearchCriteriaMutationResponseSchema,
  DashboardStatsResponseSchema,
  DocumentPaymentSchema,
  DocumentPaymentsQuerySchema,
  PaymentEligibilityResponseSchema,
  BulkCreateDocumentPaymentsBodySchema,
  BulkCreateDocumentPaymentsResponseSchema,
  SendDocumentByEmailBodySchema,
  SendDocumentByEmailResponseSchema,
  EmailAutocompleteQuerySchema,
  EmailAutocompleteResponseSchema,
  OcrTextMapQuerySchema,
  OcrTextMapResponseSchema,
  GridPresetsQuerySchema,
  GridPresetPathSchema,
  GridPresetTypeQuerySchema,
  GridPresetsResponseSchema,
  GridPresetResponseSchema,
  GridPresetMutationResponseSchema,
  SaveGridPresetBodySchema,
  DeleteGridPresetBodySchema,
} from '@/domain/documents/schemas';

export const documentsApi = {
  // GET documents/{id}/accept/result — publish background-task result (DA-19604).
  // Backup completion signal for clients whose WebSocket is blocked; the body has
  // the same shape as the DOCUMENT_PUBLISH_COMPLETED event. A 404 means no result
  // yet (never published in the last hour, or the record expired).
  getAcceptResult: endpoint({
    method: 'GET',
    url: (p: { documentID: string }) => `documents/${p.documentID}/accept/result`,
    path: AcceptResultPathSchema,
    response: AcceptResultResponseSchema,
  }),
  // GET document-subtypes — assignable sub-type catalog for the reclassify menu.
  // Labels are backend-owned brain tags (NOT i18n).
  getSubtypes: endpoint({
    method: 'GET',
    url: 'document-subtypes',
    response: SubtypeCatalogResponseSchema,
  }),
  // GET documents/aggregations — view-agnostic categorization summary (doctype tabs + sub-type
  // chips + active-category count) the tab bar refreshes WITHOUT paging documents (da-19295 D9).
  // Renamed from the legacy grid/documents/total. Global text/date filter rides via the URL
  // query; the active sub-type rides as subtags[] to pin its chip only (not to narrow counts/tabs);
  // NO filterModel.
  getDocumentsAggregations: endpoint({
    method: 'GET',
    url: 'documents/aggregations',
    query: DocumentsAggregationsQuerySchema,
    response: DocumentsAggregationsResponseSchema,
  }),
  // GET searchGetHints?prefix= — tag/user autocomplete hints (#-prefixed search). Same
  // base (/v2/3/) as the legacy rcInstance path; CompanyId rides via the request header.
  getTagSuggestions: endpoint({
    method: 'GET',
    url: 'searchGetHints',
    query: TagSuggestionsQuerySchema,
    response: TagSuggestionsResponseSchema,
  }),
  // GET getMRUTags — recently used tags. Same /v2/3/ base as the legacy request path; CompanyId rides the
  // request header.
  getRecentTags: endpoint({
    method: 'GET',
    url: 'getMRUTags',
    response: RecentTagsResponseSchema,
  }),
  // GET exportFormats — the export menu's formats. Same /v2/3/ base as the legacy request path; CompanyId rides
  // the request header.
  getExportFormats: endpoint({
    method: 'GET',
    url: 'exportFormats',
    response: ExportFormatsResponseSchema,
  }),
  // GET getNewSearchFavorites — sidebar FavoritesMenu list (system + saved searches).
  // Same /v2/3/ base as the legacy rcInstance path; CompanyId rides the request header.
  getSearchFavorites: endpoint({
    method: 'GET',
    url: 'getNewSearchFavorites',
    response: SearchFavoritesResponseSchema,
  }),
  // POST saveSearchCriteria — persist a saved search. Response is not consumed (the list is
  // invalidated → refetched, matching the legacy ensureSaveSearchCriteria refetch).
  saveSearchCriteria: endpoint({
    method: 'POST',
    url: 'saveSearchCriteria',
    body: SaveSearchCriteriaBodySchema,
    response: SearchCriteriaMutationResponseSchema,
  }),
  // POST deleteSearchCriteria — remove a saved search by ID. Response is not consumed
  // (the cache row is dropped by the ID we already hold).
  deleteSearchCriteria: endpoint({
    method: 'POST',
    url: 'deleteSearchCriteria',
    body: DeleteSearchCriteriaBodySchema,
    response: SearchCriteriaMutationResponseSchema,
  }),
  // GET getDashboardStats — document processing/count snapshot; read-only. Feeds the
  // Configurations → General fin-toggle guard. Same /v2/3/ base; CompanyId rides the header.
  getDashboardStats: endpoint({
    method: 'GET',
    url: 'getDashboardStats',
    response: DashboardStatsResponseSchema,
  }),
  // POST payments?documentID= — stage a payment for this invoice (nothing is sent to the bank;
  // it lands in Pay with status PREPARATION). The response carries the new payment id, which the
  // invoice page deep-links to as "Go to payment form" (DPM-656). No request body.
  createDocumentPayment: endpoint({
    method: 'POST',
    url: 'payments',
    query: DocumentPaymentsQuerySchema,
    response: DocumentPaymentSchema,
  }),
  // GET payments/eligibility-check?documentID= — whether a payment may be prepared for this
  // invoice, and if not, which conditions block it. Drives the Prepare-payment button.
  checkPaymentEligibility: endpoint({
    method: 'GET',
    url: 'payments/eligibility-check',
    query: DocumentPaymentsQuerySchema,
    response: PaymentEligibilityResponseSchema,
  }),
  // POST payments/bulk — stage payments for a whole selection (workspace bulk panel). Responds
  // with a counter summary, not payment entities.
  bulkCreateDocumentPayments: endpoint({
    method: 'POST',
    url: 'payments/bulk',
    body: BulkCreateDocumentPaymentsBodySchema,
    response: BulkCreateDocumentPaymentsResponseSchema,
  }),
  // POST send_document_email — forward documents by email (send-by-email dialog).
  // The response is not consumed: the dialog closes on success and the toast is
  // built from the request body.
  sendDocumentByEmail: endpoint({
    method: 'POST',
    url: 'send_document_email',
    body: SendDocumentByEmailBodySchema,
    response: SendDocumentByEmailResponseSchema,
  }),
  // GET email_autocomplete — addresses to offer in the send-by-email dialog.
  // `document_ids` MUST go as repeated keys: backend confirmed this endpoint does
  // not parse `document_ids[]`, which is the form axios produces by default and
  // the form every other array query param in this app uses.
  getEmailAutocomplete: endpoint({
    method: 'GET',
    url: 'email_autocomplete',
    query: EmailAutocompleteQuerySchema,
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    response: EmailAutocompleteResponseSchema,
  }),
  // GET getTextMap?documentID= — the OCR text map of the document's pages.
  getOcrTextMap: endpoint({
    method: 'GET',
    url: 'getTextMap',
    query: OcrTextMapQuerySchema,
    response: OcrTextMapResponseSchema,
  }),
  // Documents workspace grid presets. Same /v2/3/ base as the legacy request path; CompanyId rides the header,
  // and the list holds this company's presets plus the "(all)" ones.
  // GET configs?type=grid&version=be_driven_grid — the presets list.
  getGridPresets: endpoint({
    method: 'GET',
    url: 'configs',
    query: GridPresetsQuerySchema,
    response: GridPresetsResponseSchema,
  }),
  // GET configs/{id}?type=grid — one preset, for a shared link (it may belong to another user).
  getGridPreset: endpoint({
    method: 'GET',
    url: (p: { id: string }) => `configs/${encodeURIComponent(p.id)}`,
    path: GridPresetPathSchema,
    query: GridPresetTypeQuerySchema,
    response: GridPresetResponseSchema,
  }),
  // POST configs — a new preset (no id).
  createGridPreset: endpoint({
    method: 'POST',
    url: 'configs',
    query: GridPresetsQuerySchema,
    body: SaveGridPresetBodySchema,
    response: GridPresetMutationResponseSchema,
  }),
  // PUT configs — overwrite an existing preset (id in the body).
  updateGridPreset: endpoint({
    method: 'PUT',
    url: 'configs',
    query: GridPresetsQuerySchema,
    body: SaveGridPresetBodySchema,
    response: GridPresetMutationResponseSchema,
  }),
  // DELETE configs?type=grid with { id } in the body.
  deleteGridPreset: endpoint({
    method: 'DELETE',
    url: 'configs',
    query: GridPresetTypeQuerySchema,
    body: DeleteGridPresetBodySchema,
    response: GridPresetMutationResponseSchema,
  }),
};
